// SIMPLIFIED AVAILABILITY CHECKER - SINGLE DATA SOURCE
// Uses only reservations collection - no dual data checking
// FIXED: Shows extended bikes in results with red color marking
// Extended bikes are VISIBLE but marked as extended with red styling

import React, { useState, useEffect } from "react";
import { Search, Calendar, CheckCircle, AlertCircle, Loader, Building, Shield, Bike, Clock } from "lucide-react";
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase';

// Simplified AvailabilityChecker with single data source
const SimplifiedAvailabilityChecker = ({ 
  currentUser, 
  currentCompany, 
  getCompanyContext 
}) => {
  // Get company context for data isolation
  const companyId = getCompanyContext ? getCompanyContext() : null;

  // State Management
  const [bikes, setBikes] = useState([]);
  const [bikeModels, setBikeModels] = useState([]);
  const [availabilityModel, setAvailabilityModel] = useState("");
  const [availabilityStartDate, setAvailabilityStartDate] = useState("");
  const [availabilityEndDate, setAvailabilityEndDate] = useState("");
  const [availableBikes, setAvailableBikes] = useState([]);
  const [bookedBikes, setBookedBikes] = useState(new Set());
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [extendedBikes, setExtendedBikes] = useState(new Map()); // Track overdue/extended bikes

  // Firebase collections - SIMPLIFIED: Only need these two
  const managedBikesCollection = collection(db, 'managedBikes');
  const reservationsCollection = collection(db, `companies/${companyId}/reservations`);

  // Permission check
  const hasPermission = (permission) => {
    if (currentUser?.permissions?.includes(permission)) return true;
    if (currentUser?.role === 'admin') return true;
    return false;
  };

  // Permission guard
  if (!hasPermission('bookings')) {
    return (
      <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <Search className="w-5 h-5 mr-2 text-orange-400" />
            Check Motorbike Availability
            <span className="ml-2 text-xs bg-red-600 text-white px-2 py-1 rounded-full">
              Access Denied
            </span>
          </h2>
        </div>
        <div className="text-center py-12">
          <Shield className="mx-auto h-20 w-20 text-gray-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Access Denied</h3>
          <p className="text-gray-500 text-lg">You don't have permission to check bike availability.</p>
        </div>
      </div>
    );
  }

  // Company context validation
  if (!companyId || !currentCompany) {
    return (
      <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <Search className="w-5 h-5 mr-2 text-orange-400" />
            Check Motorbike Availability
            <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
              Simplified
            </span>
          </h2>
        </div>
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-20 w-20 text-yellow-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Company Context Missing</h3>
          <p className="text-gray-500 text-lg">Please ensure you're logged in with a valid company.</p>
        </div>
      </div>
    );
  }

  // Load company bikes from Firebase
  useEffect(() => {
    if (!companyId) return;

    console.log(`Loading bikes for company: ${currentCompany.name} (${companyId})`);
    
    const q = query(managedBikesCollection, where('companyId', '==', companyId));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const loadedBikes = [];
      querySnapshot.forEach((doc) => {
        loadedBikes.push({ id: doc.id, ...doc.data() });
      });
      
      setBikes(loadedBikes);
      
      // Extract unique models
      const models = [...new Set(loadedBikes.map(bike => bike.model))].sort();
      setBikeModels(models);
      
      setIsInitialLoading(false);
      console.log(`Loaded ${loadedBikes.length} bikes with ${models.length} models for ${currentCompany.name}`);
    }, (error) => {
      console.error('Error loading bikes:', error);
      setAvailabilityError('Error loading bikes. Please refresh the page.');
      setIsInitialLoading(false);
    });

    return () => unsubscribe();
  }, [companyId]);

  // CORRECTED: Check availability and mark extended bikes
  const checkSimplifiedAvailability = async () => {
    setCheckingAvailability(true);
    setAvailabilityError('');
    setAvailableBikes([]);
    setBookedBikes(new Set());
    setExtendedBikes(new Map());
    
    // Validate dates
    if (!availabilityStartDate || !availabilityEndDate) {
      setAvailabilityError("Please select both start and end dates");
      setCheckingAvailability(false);
      return;
    }
    
    const startDate = new Date(availabilityStartDate);
    const endDate = new Date(availabilityEndDate);
    
    if (startDate > endDate) {
      setAvailabilityError("End date must be after start date");
      setCheckingAvailability(false);
      return;
    }
    
    try {
      console.log(`🔍 Checking simplified availability for ${currentCompany.name}`);
      
      // Filter bikes by model if selected
      let filteredBikes = [...bikes];
      if (availabilityModel) {
        filteredBikes = filteredBikes.filter(bike => bike.model === availabilityModel);
      }
      
      // Parse and normalize check dates ONCE
      const checkStart = new Date(availabilityStartDate);
      const checkEnd = new Date(availabilityEndDate);
      checkStart.setHours(0, 0, 0, 0);
      checkEnd.setHours(0, 0, 0, 0);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Get all booked reservations
      const bookedReservationsQuery = query(
        reservationsCollection,
        where('availabilityStatus', '==', 'booked')
      );
      
      const reservationsSnapshot = await getDocs(bookedReservationsQuery);
      const bookedBikesSet = new Set();
      const extendedBikesMap = new Map();
      
      // Check each reservation for date conflicts
      reservationsSnapshot.forEach((doc) => {
        const reservation = doc.data();
        
        // Skip cancelled or completed reservations
        if (reservation.availabilityStatus === 'cancel' || 
            reservation.rentalStatus === 'completed') {
          return;
        }
        
        if (!reservation.pickupDateTime || !reservation.dropoffDateTime || !reservation.motorbikeNumber) {
          return;
        }
        
        // Parse and normalize reservation dates
        const reservationStart = new Date(reservation.pickupDateTime);
        const reservationEnd = new Date(reservation.dropoffDateTime);
        
        // Normalize to start of day (midnight)
        reservationStart.setHours(0, 0, 0, 0);
        reservationEnd.setHours(0, 0, 0, 0);
        
        // Check if bike is overdue (dropoff date has passed)
        const isOverdue = today > reservationEnd;
        let daysOverdue = 0;
        
        if (isOverdue) {
          const diffTime = today - reservationEnd;
          daysOverdue = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        }
        
        // Check for overlap between reservation period and check period
        // For overdue: bike blocks all future dates
        // For normal: bike blocks only during reservation period
        let hasConflict;
        
        if (isOverdue) {
          // OVERDUE: Bike blocks from pickup through any future date
          hasConflict = reservationStart <= checkEnd;
        } else {
          // NORMAL: Bike blocks during reservation period (inclusive of dropoff date)
          hasConflict = reservationStart <= checkEnd && reservationEnd >= checkStart;
        }
        
        if (hasConflict) {
          bookedBikesSet.add(reservation.motorbikeNumber);
          
          // Mark as extended if overdue
          if (isOverdue) {
            extendedBikesMap.set(reservation.motorbikeNumber, {
              daysOverdue: daysOverdue,
              customerName: reservation.customerName,
              originalEndDate: reservationEnd,
              reservationId: reservation.id
            });
          }
          
          console.log(`🔒 Bike ${reservation.motorbikeNumber} ${isOverdue ? '(EXTENDED)' : '(BOOKED)'}:`, {
            reservation: `${reservationStart.toLocaleDateString()} to ${reservationEnd.toLocaleDateString()}`,
            checking: `${checkStart.toLocaleDateString()} to ${checkEnd.toLocaleDateString()}`,
            overdue: isOverdue ? `${daysOverdue} days` : 'No'
          });
        }
      });
      
      // Separate available bikes from booked bikes
      const availableBikesResult = filteredBikes.filter(bike => {
        const bikeIdentifier = bike.regNumber || bike.id;
        return !bookedBikesSet.has(bikeIdentifier);
      });
      
      setAvailableBikes(availableBikesResult);
      setBookedBikes(bookedBikesSet);
      setExtendedBikes(extendedBikesMap);
      
      console.log(`✅ Availability check completed:`, {
        totalBikes: filteredBikes.length,
        bookedBikes: bookedBikesSet.size,
        extendedBikes: extendedBikesMap.size,
        availableBikes: availableBikesResult.length,
        dateRange: `${checkStart.toLocaleDateString()} to ${checkEnd.toLocaleDateString()}`
      });
      
    } catch (error) {
      console.error(`❌ Error checking availability:`, error);
      setAvailabilityError(`Error checking availability: ${error.message}`);
      setAvailableBikes([]);
      setBookedBikes(new Set());
      setExtendedBikes(new Map());
    } finally {
      setCheckingAvailability(false);
    }
  };

  // Check if individual bike is available
  const isIndividualBikeAvailable = (bike) => {
    if (!availabilityStartDate || !availabilityEndDate) return true;
    const bikeIdentifier = bike.regNumber || bike.id;
    return !bookedBikes.has(bikeIdentifier);
  };

  // Check if bike is extended/overdue
  const getBikeExtendedInfo = (bike) => {
    const bikeIdentifier = bike.regNumber || bike.id;
    return extendedBikes.get(bikeIdentifier);
  };

  const availableBikesCount = bikes.filter(bike => 
    (bike.status || 'available').toLowerCase() === 'available'
  ).length;

  // Loading state
  if (isInitialLoading) {
    return (
      <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <Search className="w-5 h-5 mr-2 text-orange-400" />
            Check Motorbike Availability
          </h2>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center py-12">
            <Loader className="animate-spin text-orange-500 mr-3" size={32} />
            <div>
              <h3 className="text-white font-medium">Loading System...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No bikes state
  if (bikes.length === 0) {
    return (
      <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <Search className="w-5 h-5 mr-2 text-orange-400" />
            Check Motorbike Availability
          </h2>
        </div>
        <div className="text-center py-12">
          <Bike className="mx-auto h-20 w-20 text-gray-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">No Bikes Available</h3>
          <p className="text-gray-500 text-lg">No bikes found for {currentCompany.name}.</p>
        </div>
      </div>
    );
  }

  // Determine which bikes to display
  const displayBikes = availabilityStartDate && availabilityEndDate 
    ? (availabilityModel ? bikes.filter(b => b.model === availabilityModel) : bikes)
    : bikes.slice(0, 6);

  // Main render
  return (
    <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
      <div className="p-6 border-b border-gray-700">
        <h2 className="text-xl font-semibold text-white flex items-center">
          <Search className="w-5 h-5 mr-2 text-orange-400" />
          Check Motorbike Availability
          <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
            Simplified
          </span>
          <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded-full">
            Fixed
          </span>
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Extended bikes shown in red • Bikes unavailable on dropoff date
        </p>
        <div className="mt-2 flex items-center text-blue-400 text-sm">
          <Building size={16} className="mr-1" />
          <span>{currentCompany.name} • {bikes.length} bikes</span>
        </div>
      </div>
      
      <div className="p-6">
        <div className="mb-6 bg-blue-900/30 border border-blue-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <h3 className="text-blue-300 font-medium mb-2">
                Simplified System: {currentCompany.name}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-blue-200">Data Source:</span>
                  <div className="text-gray-300">✓ Reservations only</div>
                </div>
                <div>
                  <span className="text-blue-200">Fleet:</span>
                  <div className="text-gray-300">{availableBikesCount} available</div>
                </div>
                <div>
                  <span className="text-blue-200">Display:</span>
                  <div className="text-green-300">✓ Shows all with status</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">Model</label>
            <select 
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              value={availabilityModel}
              onChange={(e) => setAvailabilityModel(e.target.value)}
            >
              <option value="">All Models</option>
              {bikeModels.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">Start Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={availabilityStartDate}
                onChange={(e) => setAvailabilityStartDate(e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">End Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={availabilityEndDate}
                onChange={(e) => setAvailabilityEndDate(e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
          </div>
        </div>

        {availabilityError && (
          <div className="mb-6 p-3 bg-red-900/30 border border-red-600 rounded-lg text-red-200 flex items-center">
            <AlertCircle className="mr-2 w-5 h-5" />
            <span>{availabilityError}</span>
          </div>
        )}

        <button 
          onClick={checkSimplifiedAvailability}
          disabled={checkingAvailability}
          className="w-full mb-6 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-medium px-4 py-3 rounded-lg disabled:opacity-50 flex items-center justify-center"
        >
          {checkingAvailability ? (
            <>
              <Loader className="animate-spin mr-2" size={18} />
              Checking...
            </>
          ) : (
            <>
              Check Availability
              <Shield className="ml-2" size={16} />
            </>
          )}
        </button>

        <div>
          <h3 className="font-semibold mb-4 text-white flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
            {availabilityStartDate && availabilityEndDate ? 'Results' : 'Bikes'} for {currentCompany.name}
            {availabilityStartDate && availabilityEndDate && (
              <>
                <span className="ml-3 text-sm text-gray-400">
                  {availableBikes.length} available
                </span>
                <span className="ml-3 text-sm text-red-400">
                  {bookedBikes.size} booked
                </span>
                {extendedBikes.size > 0 && (
                  <span className="ml-3 text-sm text-red-500 font-semibold">
                    {extendedBikes.size} extended
                  </span>
                )}
              </>
            )}
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayBikes.map(bike => {
              if (!bike) return null;
              
              const isAvailable = isIndividualBikeAvailable(bike);
              const extendedInfo = getBikeExtendedInfo(bike);
              
              return (
                <SimplifiedBikeCard
                  key={bike.id}
                  bike={bike}
                  isAvailable={isAvailable}
                  extendedInfo={extendedInfo}
                  availabilityStartDate={availabilityStartDate}
                  availabilityEndDate={availabilityEndDate}
                  companyId={companyId}
                  companyName={currentCompany.name}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// SimplifiedBikeCard component - Shows ALL bikes with extended ones marked in red
const SimplifiedBikeCard = React.memo(({ 
  bike, 
  isAvailable, 
  extendedInfo,
  availabilityStartDate, 
  availabilityEndDate,
  companyId,
  companyName
}) => {
  const handleCreateInvoice = () => {
    if (!isAvailable) return;
    
    const invoiceData = {
      bikeModel: bike.model,
      bikeId: bike.id,
      regNumber: bike.regNumber,
      dailyRate: bike.dailyRate,
      pickupDate: availabilityStartDate,
      dropoffDate: availabilityEndDate,
      companyId: companyId,
      companyName: companyName,
      dataSource: 'simplified',
      singleDataSource: true
    };
    
    localStorage.setItem('invoiceData', JSON.stringify(invoiceData));
    localStorage.setItem('invoiceCompanyContext', JSON.stringify({
      companyId: companyId,
      companyName: companyName
    }));
    
    window.location.href = '/invoice.html';
  };

  // Determine card styling based on extended status
  const getCardBorderClass = () => {
    if (isAvailable) return "border-gray-600 hover:border-orange-500";
    if (extendedInfo) return "border-red-500"; // Red border for extended
    return "border-red-700 opacity-60"; // Regular booked
  };

  return (
    <div className={`bg-gray-700 border-2 ${getCardBorderClass()} rounded-lg overflow-hidden transition-all`}>
      <div className={`p-3 text-center border-b ${extendedInfo ? 'bg-red-900/30 border-red-700' : 'bg-gray-600 border-gray-500'}`}>
        <span className="font-bold text-white">{bike.model}</span>
        <div className="text-xs text-blue-300 flex items-center justify-center mt-1">
          <Building size={12} className="mr-1" />
          {companyName}
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-300">Registration:</span>
          <span className="font-medium text-white">{bike.regNumber}</span>
        </div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-300">Daily Rate:</span>
          <span className="font-medium text-white">${bike.dailyRate}</span>
        </div>
        {bike.color && (
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-300">Color:</span>
            <span className="font-medium text-white">{bike.color}</span>
          </div>
        )}
        
        {/* Status Section - Enhanced with Extended Info */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-300">Status:</span>
            {isAvailable ? (
              <span className="bg-green-900 text-green-400 text-xs px-2 py-1 rounded-full flex items-center">
                <CheckCircle size={12} className="mr-1" />
                Available
              </span>
            ) : extendedInfo ? (
              <span className="bg-red-900 text-red-300 text-xs px-2 py-1 rounded-full flex items-center font-semibold">
                <Clock size={12} className="mr-1" />
                Extended
              </span>
            ) : (
              <span className="bg-red-900 text-red-400 text-xs px-2 py-1 rounded-full flex items-center">
                <AlertCircle size={12} className="mr-1" />
                Booked
              </span>
            )}
          </div>
          
          {/* Extended/Overdue Details in Status Area - RED THEME */}
          {extendedInfo && (
            <div className="mt-2 p-3 bg-red-900/40 border-2 border-red-500 rounded-lg">
              <div className="flex items-center gap-1 mb-2">
                <Clock size={14} className="text-red-300" />
                <span className="text-sm font-bold text-red-200">EXTENDED RENTAL</span>
              </div>
              <div className="text-xs text-red-100 space-y-1">
                <div className="font-bold text-base text-red-200">
                  {extendedInfo.daysOverdue} days overdue
                </div>
                {extendedInfo.customerName && (
                  <div className="text-red-200">
                    Customer: <span className="font-medium">{extendedInfo.customerName}</span>
                  </div>
                )}
                <div className="text-red-200">
                  Original due: <span className="font-medium">{extendedInfo.originalEndDate?.toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="mb-3 p-2 bg-blue-900/20 border border-blue-700 rounded text-center">
          <div className="text-xs text-blue-300 flex items-center justify-center">
            <Shield size={12} className="mr-1" />
            {companyName}
          </div>
        </div>
        <button
          onClick={handleCreateInvoice}
          disabled={!isAvailable}
          className={`w-full text-center ${isAvailable ? "bg-orange-500 hover:bg-orange-600" : "bg-gray-600 cursor-not-allowed opacity-50"} text-white font-medium py-2 rounded-lg transition-colors`}
        >
          {isAvailable ? "Create Invoice" : extendedInfo ? "Extended - Not Available" : "Booked - Not Available"}
        </button>
      </div>
    </div>
  );
});

export default SimplifiedAvailabilityChecker;